// Shared HERE Maps building blocks used by the map Stimulus controllers, so each
// controller stays close to the small Google-based original it replaces.
import { loadHereMaps, herePlatform } from "./here_maps_loader"

// Load HERE, then build an interactive vector map centered as requested.
// Returns { H, map, ui, behavior }. Call from a controller's connect().
export async function createHereMap(element, { center, zoom = 6, interactive = true }) {
  await loadHereMaps()
  const H = window.H
  const layers = herePlatform().createDefaultLayers()

  // A Turbo snapshot restore (browser back) replays the cached DOM, which still holds the dead
  // canvas of the previous map instance. Always start from an empty container, or the fresh map
  // is appended *below* the corpse and the card looks blank. (Don't try to clean this up on
  // turbo:before-cache instead: a frame link with turbo-action=advance renders the frame first
  // and caches after, so that hook fires *after* the new map exists and would kill it.)
  element.replaceChildren()

  const map = new H.Map(element, layers.vector.normal.map, {
    zoom,
    center,
    pixelRatio: window.devicePixelRatio || 1,
  })
  map.__element = element

  // HERE sizes its viewport once, at construction. Watch the container too, so a map that
  // boots inside a not-yet-sized element (offcanvas, nested-form row, tab panel) catches up
  // instead of silently rendering blank.
  const onResize = () => map.getViewPort().resize()
  window.addEventListener("resize", onResize)
  const observer = new ResizeObserver(onResize)
  observer.observe(element)
  map.__onResize = onResize
  map.__resizeObserver = observer

  const behavior = interactive ? new H.mapevents.Behavior(new H.mapevents.MapEvents(map)) : null
  const ui = interactive ? H.ui.UI.createDefault(map, layers) : null
  if (ui) configureBaseLayers(H, ui, layers)
  if (interactive) addFullscreenControl(map, element)

  return { H, map, ui, behavior }
}

// Google's default UI shipped a fullscreen button; HERE's does not. Rebuild it as a plain
// control inside the map container, so it stays reachable once the element is in the top layer.
const EXPAND_ICON = `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>`

const COLLAPSE_ICON = `<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>`

function addFullscreenControl(map, element) {
  if (!element.requestFullscreen) return

  if (getComputedStyle(element).position === "static") element.style.position = "relative"

  const button = document.createElement("button")
  button.type = "button"
  button.className =
    "absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-600 shadow-md hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-green-500"

  const active = () => document.fullscreenElement === element

  const sync = () => {
    button.innerHTML = active() ? COLLAPSE_ICON : EXPAND_ICON
    button.title = active() ? "Vollbild beenden" : "Vollbild"
    button.setAttribute("aria-label", button.title)
  }

  // The map element gets its height from CSS (a flex-1 inside a height-constrained card, or an
  // h-* class). Override it while fullscreen and put it back on exit — restoring an empty string
  // simply hands the height back to the stylesheet. The ResizeObserver above re-fits the viewport.
  let previousHeight = ""

  button.addEventListener("click", () => {
    if (active()) {
      document.exitFullscreen()
    } else {
      previousHeight = element.style.height
      element.requestFullscreen()
    }
  })

  const onFullscreenChange = () => {
    element.style.height = active() ? "100%" : previousHeight
    sync()
  }

  document.addEventListener("fullscreenchange", onFullscreenChange)
  map.__onFullscreenChange = onFullscreenChange

  sync()
  element.appendChild(button)
}

// Give the map-settings control (gear icon) a street/satellite/hybrid base-map
// switcher. HERE's default street map is a vector layer, but its default
// `raster.satellite.*` layers use the deprecated Map Tile API v2.1 (HTTP 410) — so
// the aerial layers are built from the modern Raster Tile API v3 instead
// (satellite.day = imagery, explore.satellite.day = imagery + roads/labels).
function satelliteLayer(H, style) {
  const service = herePlatform().getRasterTileService({ queryParams: { style, size: 512 } })
  return new H.map.layer.TileLayer(new H.service.rasterTile.Provider(service))
}

function configureBaseLayers(H, ui, layers) {
  const existing = ui.getControl("mapsettings")
  if (existing) ui.removeControl("mapsettings")

  ui.addControl(
    "mapsettings",
    new H.ui.MapSettingsControl({
      baseLayers: [
        { label: "Karte", layer: layers.vector.normal.map },
        { label: "Satellit", layer: satelliteLayer(H, "satellite.day") },
        { label: "Hybrid", layer: satelliteLayer(H, "explore.satellite.day") },
      ],
    }),
  )
}

// Colored circle marker icon (replaces google.maps.SymbolPath.CIRCLE styling).
export function circleIcon(color, radius = 7) {
  const size = (radius + 3) * 2
  const c = size / 2
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
    <circle cx="${c}" cy="${c}" r="${radius}" fill="${color}" stroke="#ffffff" stroke-width="2"/>
  </svg>`
  return new window.H.map.Icon(svg)
}

// Heading arrow marker icon (replaces google.maps.SymbolPath.FORWARD_CLOSED_ARROW).
// `rotation` is degrees clockwise from north.
export function arrowIcon(color, rotation = 0) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <g transform="rotate(${rotation} 14 14)">
      <path d="M14 3 L21 24 L14 19 L7 24 Z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
    </g>
  </svg>`
  return new window.H.map.Icon(svg, { anchor: { x: 14, y: 14 } })
}

// A path through an ordered list of { lat, lng } points (a GPS trail). Returns the
// H.map.Polyline so the caller can add it to a group for fitToObjects.
export function polyline(H, points, { color = "#009C72", width = 4 } = {}) {
  const lineString = new H.geo.LineString()
  points.forEach((p) => lineString.pushPoint({ lat: p.lat, lng: p.lng }))
  return new H.map.Polyline(lineString, {
    style: { lineWidth: width, strokeColor: color },
  })
}

// One reusable InfoBubble per map (mirrors a single google.maps.InfoWindow).
export function singleBubble(H, ui) {
  let bubble = null
  return (position, content) => {
    if (!bubble) {
      bubble = new H.ui.InfoBubble(position, { content })
      ui.addBubble(bubble)
    } else {
      bubble.setPosition(position)
      bubble.setContent(content)
      bubble.open()
    }
  }
}

// Marker popup content as DOM nodes rather than an HTML string: every value in `rows` is
// user input (site names, task names, addresses), so it must never reach innerHTML.
// `rows` is [[label, value], ...] — a row with a blank value is dropped.
export function detailBubble(rows, { href, linkLabel }) {
  const node = document.createElement("div")
  node.className = "w-56 flex flex-col text-sm"

  rows.forEach(([label, value]) => {
    if (!value) return
    const row = document.createElement("p")
    const strong = document.createElement("strong")
    strong.textContent = label
    row.append(strong, ` ${value}`)
    node.appendChild(row)
  })

  const link = document.createElement("a")
  link.href = href
  link.setAttribute("data-turbo-frame", "_top")
  link.className = "text-bautask-green underline mt-2 self-end"
  link.textContent = linkLabel
  node.appendChild(link)

  return node
}

// Zoom/pan so every object is visible (mirrors map.fitBounds over a set of markers).
export function fitToObjects(H, map, objects) {
  if (!objects.length) return
  const group = new H.map.Group()
  map.addObject(group)
  group.addObjects(objects)
  if (objects.length > 1) {
    map.getViewModel().setLookAtData({ bounds: group.getBoundingBox() })
  }
}

// Detach every listener createHereMap added, dispose the engine, and empty the container so no
// dead canvas survives into a Turbo snapshot. Safe to call more than once.
export function disposeHereMap(map) {
  if (!map || map.__disposed) return
  map.__disposed = true

  if (map.__onResize) window.removeEventListener("resize", map.__onResize)
  if (map.__resizeObserver) map.__resizeObserver.disconnect()
  if (map.__onFullscreenChange) {
    document.removeEventListener("fullscreenchange", map.__onFullscreenChange)
  }

  map.dispose()
  if (map.__element) map.__element.replaceChildren()
}
